import {
  Button,
  Center,
  Container,
  Flex,
  Loader,
  Notification,
  PasswordInput,
  Stack,
  Text,
  Title,
  Transition,
} from "@mantine/core";
import { useEffect, useState } from "react";
import SmartWizLogo from "../../components/Logos/SmartWizLogo";
import { useNavigate, useParams } from "react-router-dom";
import {
  checkIfResetPasswordTokenValid,
  updatePassword,
} from "../../utils/api";
import PasswordRequirements, {
  isLocallyValidPassword,
} from "../../components/PasswordRequirements";
import { IconCircleCheckFilled, IconLock } from "@tabler/icons-react";
import { useBranding } from "../LoginManagement/hooks/useBranding";
import AuthLogo from "@/components/WhiteLabel/AuthLogo";
import BrandedAnchor from "@/components/WhiteLabel/BrandedAnchor";
import { AppRoutes } from "@/constants.ts";

const getSafeNextUrl = (rawNextUrl: string | null): string | null => {
  if (!rawNextUrl) return null;
  if (
    rawNextUrl.startsWith("//") ||
    rawNextUrl.startsWith("\\") ||
    rawNextUrl.startsWith("/\\")
  ) {
    return null;
  }
  if (!rawNextUrl.startsWith("/") && !/^https?:\/\//i.test(rawNextUrl)) {
    return null;
  }

  try {
    const parsedUrl = new URL(rawNextUrl, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) return null;
    if (!parsedUrl.pathname.startsWith("/")) return null;

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return null;
  }
};

const ConfirmResetPassword = () => {
  const { businessSlug } = useParams();
  const [password, setPassword] = useState("");
  const [reenteredPassword, setReenteredPassword] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
    loading?: boolean;
    withCloseButton?: boolean;
  } | null>(null);
  const {
    businessInfo,
    loading: businessLoading,
    error: businessError,
  } = useBranding();

  const [error, setError] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);

  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  const token = queryParams.get("token");
  const nextUrl = queryParams.get("next");
  const safeNextUrl = getSafeNextUrl(nextUrl);

  useEffect(() => {
    const fetchData = async () => {
      setNotification(null);
      const response = await checkIfResetPasswordTokenValid(token);
      console.log(response);
      if (response?.status === 200) {
        setNotification(null);
      } else {
        setNotification({
          title: "Invalid Link",
          message: "To get a valid link go back to the forgot password page",
          color: "red",
          withCloseButton: false,
        });
        //disable submit button
        setDisableSubmit(true);
      }
    };
    fetchData();
  }, []);

  const submit = async (e: any) => {
    setShowVerificationMessage(false);
    e.preventDefault();
    setError({ newPassword: "", confirmPassword: "" });
    if (password !== reenteredPassword) {
      setError({
        newPassword: "",
        confirmPassword: "Passwords do not match",
      });
    } else if (!isLocallyValidPassword(password)) {
      setNotification({
        title: "Error",
        message: "Password does not meet requirements",
        color: "red",
      });
    } else {
      setNotification({
        title: "Updating Password",
        message: "Please wait while we update your password.",
        color: "blue",
        loading: true,
      });
      const response = await updatePassword(token, password);
      if (response?.status === 200) {
        setNotification({
          title: "Password Updated Successfully",
          message: "You can now login with your new password",
          color: "teal",
        });
        setShowVerificationMessage(true);
      } else {
        if (
          (response as any).response?.data?.message ===
          "New password is required"
        ) {
          setNotification({
            title: "Error",
            message: "New password is required",
            color: "red",
          });
        } else if (
          (response as any).response?.data?.message ===
          "Password must be at least 8 characters"
        ) {
          setNotification({
            title: "Error",
            message: "An error occurred. Please try again.",
            color: "red",
          });
        } else if (
          (response as any).response?.data?.message ===
          "Token is invalid or has expired"
        ) {
          setNotification({
            title: "Error",
            message: "Token is invalid or has expired",
            color: "red",
          });
        } else {
          setNotification({
            title: "Error",
            message: "Something went wrong. Please try again.",
            color: "red",
          });
        }
      }
    }
  };

  if (businessError) {
    return (
      <Container className="auth-form-container" size="sm">
        <Text c="red" size="lg" ta="center">
          {businessError}
        </Text>
      </Container>
    );
  }

  if (businessLoading) {
    return (
      <Container
        className=""
        size="sm"
        style={{ textAlign: "center", height: "100%" }}
      >
        <Center style={{ height: "100vh" }}>
          <SmartWizLogo /> <Loader size="md" color="blue" ml={10} />
        </Center>
      </Container>
    );
  }

  return (
    <Center h="100vh" w="100vw" mb={100}>
      <Container className="" size="xs">
        <form onSubmit={submit}>
          <Center w="100%" mb={24}>
            <AuthLogo
              logoUrl={businessInfo?.logoUrl}
              businessSlug={businessSlug}
              primaryColor={businessInfo?.theme?.primaryColor}
              size={72}
              isSmall={true}
              alt="Business Logo"
            />
          </Center>
          {!showVerificationMessage && (
            <Title order={1} ta="center" mb="md">
              Reset Your Password
            </Title>
          )}
          <Stack gap="md">
            {!showVerificationMessage ? (
              <Stack className="styled-inputs" gap="md">
                <PasswordRequirements
                  value={password}
                  setValue={setPassword}
                  placeholder="New Password"
                  name="newPassword"
                  variant="unstyled"
                  radius="md"
                  size="xs"
                  withAsterisk={false}
                />
                <PasswordInput
                  placeholder="Confirm New Password"
                  name="confirmPassword"
                  radius="md"
                  size="xs"
                  variant="unstyled"
                  className="password"
                  type="password"
                  leftSection={<IconLock color="#A3A9B0" />}
                  value={reenteredPassword}
                  error={error.confirmPassword}
                  onChange={(e) => setReenteredPassword(e.target.value)}
                />
              </Stack>
            ) : (
              <Stack align="center" gap="md">
                <Flex align="center" gap="xs">
                  <IconCircleCheckFilled
                    size={48}
                    style={{ color: "#12b886" }}
                  />
                  <Text size="24px" fw={700}>
                    YOUR PASSWORD HAS BEEN RESET
                  </Text>
                </Flex>
                <Text size="sm">
                  Return to the login screen to log in using your new
                  password.{" "}
                </Text>
              </Stack>
            )}
            {!showVerificationMessage ? (
              <>
                <Button
                  className="sign-in"
                  type="submit"
                  color={
                    businessSlug ? businessInfo?.theme?.primaryColor : "blue"
                  }
                  radius="md"
                  disabled={disableSubmit}
                >
                  Reset Password
                </Button>
                {notification && (
                  <Notification
                    title={notification.title}
                    color={notification.color}
                    onClose={() => setNotification(null)}
                    withCloseButton={notification.withCloseButton}
                    loading={notification.loading}
                    withBorder
                  >
                    {notification.message}
                  </Notification>
                )}
                <Flex justify="space-between" w="100%" maw={300} mx="auto">
                  <BrandedAnchor
                    className="link"
                    businessSlug={businessSlug}
                    businessInfo={businessInfo}
                    label="Back to Login"
                    fw={500}
                  />
                  <BrandedAnchor
                    className="link"
                    businessSlug={businessSlug}
                    businessInfo={businessInfo}
                    label="Forgot Password"
                    fw={500}
                  />
                </Flex>
              </>
            ) : (
              <Button
                className="sign-in"
                color={
                  businessSlug ? businessInfo?.theme?.primaryColor : "blue"
                }
                onClick={() => {
                  if (safeNextUrl) {
                    window.location.href = safeNextUrl;
                  } else {
                    navigate(
                      businessSlug
                        ? `/${businessSlug}${AppRoutes.login}`
                        : AppRoutes.login
                    );
                  }
                }}
                size="lg"
                radius="md"
                maw={300}
                mx="auto"
              >
                {safeNextUrl ? "Continue Setup" : "Log in Now"}
              </Button>
            )}
            <Transition
              transition="fade"
              duration={1000}
              mounted={showVerificationMessage}
            >
              {(styles) => (
                <Flex
                  className="powered-by"
                  justify="center"
                  align="center"
                  gap="xs"
                  mt={24}
                  style={styles}
                >
                  Powered by <SmartWizLogo isLoginPage={true} />
                </Flex>
              )}
            </Transition>
          </Stack>
        </form>
      </Container>
    </Center>
  );
};

export default ConfirmResetPassword;
